/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';

import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  category: 'ALL',
  min_gp: '',
  max_gp: '',
  order_by: 'gross_profit',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

function RpProductGp() {
  const mod = 'rp_product_gp';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [urlExcel, setUrlExcel] = useState('');
  const [cboCategory, setCboCategory] = useState([]);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'sku', label: 'SKU', align: 'left' },
    { id: 'name', label: 'ชื่อสินค้า', align: 'left' },
    { id: 'cat_name', label: 'หมวดหมู่', align: 'left' },
    { id: 'cost', label: 'ทุนปัจจุบัน', align: 'right' },
    { id: 'price', label: 'ราคาขายปัจจุบัน', align: 'right' },
    { id: 'sales_amount', label: 'ยอดขาย', align: 'right' },
    { id: 'total_cost', label: 'ทุน', align: 'right' },
    { id: 'gross_profit', label: '%GP', align: 'right' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      category: data.category,
      min_gp: data.min_gp,
      max_gp: data.max_gp,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('search', find.search);
    params.append('category', find.category);
    params.append('min_gp', find.min_gp);
    params.append('max_gp', find.max_gp);
    window.open(`${urlExcel}/download?${params.toString()}`, '_self');
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  // clamp 0..100 while typing; min/max peer check only on blur
  const clampRange = (value) => {
    if (value === '') return '';
    const n = parseInt(value, 10);
    if (Number.isNaN(n)) return '';
    return String(Math.max(0, Math.min(100, n)));
  };

  const blurGp = (role) => {
    const next = {
      ...find,
      min_gp: clampRange(find.min_gp),
      max_gp: clampRange(find.max_gp),
      page: 1,
    };
    if (next.min_gp !== '' && next.max_gp !== '') {
      if (role === 'min' && Number(next.min_gp) > Number(next.max_gp)) {
        next.min_gp = next.max_gp;
      }
      if (role === 'max' && Number(next.max_gp) < Number(next.min_gp)) {
        next.max_gp = next.min_gp;
      }
    }
    setFind(next);
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setUrlExcel(result.excel);
        setCboCategory(result.category || []);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <MDInput
              fullWidth
              value={find.category}
              select
              onChange={(e) => view({ ...find, category: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('element.category')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='NONE'>ไม่มีหมวดหมู่</MenuItem>
              {cboCategory.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <MDInput
              fullWidth
              value={find.min_gp}
              type='number'
              onChange={(e) =>
                setFind({
                  ...find,
                  min_gp: clampRange(e.target.value),
                  page: 1,
                })
              }
              onBlur={() => blurGp('min')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('element.min_gp')} :</InputAdornment>
                ),
                inputProps: { step: 1, min: 0, max: 100, autoComplete: 'off' },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <MDInput
              fullWidth
              value={find.max_gp}
              type='number'
              onChange={(e) =>
                setFind({
                  ...find,
                  max_gp: clampRange(e.target.value),
                  page: 1,
                })
              }
              onBlur={() => blurGp('max')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('element.max_gp')} :</InputAdornment>
                ),
                inputProps: { step: 1, min: 0, max: 100, autoComplete: 'off' },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2} lg={2}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              <MDButton variant='gradient' color='info' onClick={() => view({ ...find, page: 1 })}>
                <Icon>search</Icon>&nbsp;ค้นหา
              </MDButton>
              <MDButton variant='gradient' color='success' onClick={excel}>
                <Icon>table_view</Icon>&nbsp;Excel
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_account'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.barcode_id}>
                      <TableCell align='left' className='nowrap'>
                        {row.sku}
                      </TableCell>
                      <TableCell align='left'>
                        <MDBox className='cut' width={280}>
                          {row.name}
                        </MDBox>
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.cat_name}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.cost)}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.price)}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.sales_amount)}
                      </TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.total_cost)}
                      </TableCell>
                      <TableCell align='right' className='nowrap color-success'>
                        {Func.numberDisplay(row.gross_profit, 2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);
    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_account`), t(`menu.rp_product_gp`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpProductGp;
